import * as THREE from 'three/webgpu';
import { BAKE_ONLY } from '../indirect';
import { sunPositionFromAngles } from '../sky';
import {
  FWG_DYNAMIC_SHADOW_LAYER,
  FWG_STATIC_SHADOW_LAYER,
  POINT_SHADOW_FACES,
  SHADOW_SLOT_REFRESH_FRAMES,
  effectiveShadowUpdate,
  normalizeShadowUpdateN,
  shadowRefreshDue,
  sunShadowUpdate,
  lightBudget,
  lightCostBudget,
  lightDistanceM,
  lightShadowExtentM,
  previewPresence,
  resolveLightRig,
  resolveLightSupport,
  rigCost,
  selectLiveShadowSlots,
  shadowMapBytes,
  shadowMapSizeFor,
  SUN_SHADOW_TIERS,
  sunShadowPolicy,
  type AppliedShadowUpdate,
  type LightCostBudget,
  type SunShadowPolicy,
  type LightMobility,
  type LightRigInput,
  type LightType,
  type ResolvedLight,
  type ShadowSlot,
  type ShadowSlotCandidate,
  type ShadowSlotState,
  type Vec3Like,
} from './lightModel';
import type { WorldLightConfig, WorldSunConfig } from '../../world/schema';

/**
 * ── THE ONE PLACE A WORLD'S PUNCTUAL LIGHTS ARE CONSTRUCTED (E2) ────────────────────────────────
 *
 * `world.lighting.lights[]` → three light objects, mobility applied. The vocabulary and the
 * (type × mobility) support table are in `lightModel.ts` (PURE); this is the part that touches
 * `three`, and it is the ONLY module in the codebase that may `new THREE.PointLight` /
 * `new THREE.SpotLight` from world data. `scene.ts` calls it once at construction and again from
 * `applyWorldLights` (the editor's Lighting section); nothing else builds a light from a config.
 *
 * ── MOBILITY IS APPLIED BY PRESENCE, NEVER BY `intensity = 0` ───────────────────────────────────
 *
 * `src/render/indirect.ts` states the rule and the reason: a light at zero is **still in the light
 * set, still in the shader's loop, still costing its ~0.24 ms (unshadowed, clustered) or ~3 ms
 * (shadowed) per frame**. Removing it is what makes a static light free, and it is also what takes
 * it out of `LightsNode.customCacheKey()` so the shader is rebuilt WITHOUT it rather than keeping
 * a dead branch. So a `static` light is never parented at runtime at all: it is marked
 * `userData[BAKE_ONLY]` and handed to `scene.ts`, which parents it for the duration of a bake and
 * removes it in the bake bracket's `finally`. That mechanism ALREADY EXISTS (`?bakeLights=1`,
 * G3.2) and this builds on it rather than inventing a parallel one — including its per-frame
 * fence, `GiInfo.bakedLightLeaks`, which now watches the world's static lights for the whole
 * session instead of only after a bake.
 *
 * ── WHY THE APPLY PATH RECONCILES INSTEAD OF REBUILDING ─────────────────────────────────────────
 *
 * `LightsNode.customCacheKey()` hashes every light's `id` and `castShadow` into every render
 * object's dynamic key, and a changed key DISPOSES and rebuilds that object — a full NodeBuilder
 * run and fresh WGSL for every visible material. So an artist dragging an intensity slider must
 * not go through a light rebuild: `apply()` UPDATES an existing light of the same type and
 * presence in place (position, colour, intensity, distance, cone, penumbra — all uniforms or
 * poses, none of them in the key) and only constructs a new object when the type or the mobility's
 * presence actually changed. `WorldLightsInfo.keyMoves` reports how many lights crossed that line
 * on the last apply, so the hitch is a number rather than a surprise.
 */

/** Why one authored light was not built. DATA, not a console line — the editor renders it. */
export interface WorldLightRefusal {
  id: string;
  type: LightType;
  mobility: LightMobility;
  /** `LightSupport.note` — names the reason AND where the supported thing lives. */
  reason: string;
}

/** What the world's light rig actually resolved to. Published through the dev hook. */
export interface WorldLightsInfo {
  /** Lights this rig CLAIMS are in the scene, from the data. Compare with `parented`. */
  runtime: number;
  /**
   * ⭐ How many of this rig's lights are ACTUALLY parented right now — read off `light.parent`,
   * i.e. off the scene graph, not off the config that produced it.
   *
   * It exists because `runtime` alone is a self-report, and this repo's R1.6-D lesson is that a
   * self-report proves nothing (48,880 draws, 0 of them GI-inclusive, every self-report green).
   * "The static light is absent at runtime" is the central claim of the mobility model, and this
   * is the number that states it: `parented` must equal `runtime`, and a `static` light must never
   * contribute to either — outside a bake, when the bracket parents it deliberately.
   */
  parented: number;
  /** `static` lights: built, marked BAKE_ONLY, NOT parented — free until a bake parents them. */
  bakeOnly: number;
  /**
   * ⭐ FENCE 3 OF PREVIEW LIGHTING, AND IT IS A POSITIVE COUNTER READ OFF THE SCENE GRAPH.
   *
   * How many `static` lights are parented RIGHT NOW because preview is on — `light.parent !== null`
   * on an entry the reconcile flipped to `runtime` presence, never a self-report of the flag that
   * asked for it (R1.6-D: a self-report proves nothing).
   *
   * The owner's second clause is *"never shipped if the bake is stale"*, and this is the number
   * that states it: a Play-mode session reads **0 by construction** (the editor pushes `false` on
   * `close()`, and `GameScene.setLightPreview` is folded out of a production build entirely), and
   * an e2e can assert 0 after F2-to-Play rather than trusting the fold.
   *
   * ⚠️ It is a SUBSET of `parented`, never added to it. A previewing light IS a runtime light for
   * every purpose the frame has — it is in `runtime`, in `parented`, in `shadowCasters` if it
   * casts, and priced in `costUnits` — because it genuinely costs all of that.
   */
  previewParented: number;
  /** Of `runtime`, how many cast shadows. Budgeted separately; each is a depth pass. */
  shadowCasters: number;
  /**
   * L2. Of `shadowCasters`, how many have had their per-frame depth pass switched OFF —
   * `shadowUpdate: 'staticOnly'`. These cost ZERO in steady state, which is how an artist
   * legitimately gets more shadow casters than the budget, and it is the only lever in the whole
   * lighting design that removes milliseconds rather than counting them.
   */
  cachedShadowCasters: number;
  /**
   * B4. Of `shadowCasters`, how many are `shadowUpdate: 'budgeted'` AND can actually be budgeted
   * (stationary — a movable one reads as `everyFrame`, `effectiveShadowUpdate`). These are the
   * candidates the slot selector ranks; `liveShadowSlots` + `frozenShadowSlots` partition them
   * once a selection has been made.
   */
  budgetedShadowCasters: number;
  /** B4. Ids of the budgeted casters holding a live slot — read off the rig's entries, not off the config. */
  liveShadowSlots: string[];
  /** B4. Ids of the budgeted casters currently frozen (static geometry only, 0 traversals). */
  frozenShadowSlots: string[];
  /**
   * B4. The local budget the last selection was made against (`localShadowBudget(tier, sun)`),
   * or `null` while NO selection has been made — a rig that has never been ticked with a camera.
   * `describeLightRig` reads null as "price every budgeted light at its peak", the same rule the
   * pure fold keeps, so a headless rig and a first frame can never quote a cheaper number than
   * the frame could draw.
   */
  localShadowBudget: number | null;
  /**
   * L2. Shadow-map VRAM for THIS rig's lights (`mapSize² × 8 B × (point ? 6 : 1)`). The whole-rig
   * number, which is the one that matters because it includes the sun, is
   * `WorldLightRigInfo.cost.shadowMapBytes`.
   */
  shadowMapBytes: number;
  /** Per-tier design ceilings (`lightModel.lightBudget`) — reported, never enforced. */
  budget: { runtime: number; shadow: number };
  /** Authored lights this pipeline refused, with the reason. Never silently downgraded. */
  refusals: WorldLightRefusal[];
  /**
   * How many lights were CONSTRUCTED OR DESTROYED by the last `apply()` — i.e. how many times the
   * light cache key moved and every visible material's WGSL was regenerated. 0 means the apply was
   * pure uniform writes (a slider drag) and cost nothing.
   */
  keyMoves: number;
}

/**
 * ⭐ THE ONE SESSION-LEVEL LIGHTING OPTION, AND IT IS NOT WORLD DATA (preview lighting).
 *
 * `previewStatic` parents this world's `static` lights for real while their energy is NOT yet in
 * the bound atlas, so an artist can dial one in. It is DERIVED per session by the editor and
 * written by nothing: there is no schema field, `world.json` is byte-identical whether preview ran
 * or not, and `worldHash` therefore cannot move. Default `false` everywhere — a boot, a headless
 * rig and a shipped build all get today's exact behaviour.
 *
 * ⚠️ FLIPPING IT IS A CONSTRUCT/DESTROY PER STATIC LIGHT, i.e. `LightsNode.customCacheKey()` moves
 * and every visible material's WGSL is regenerated once. `info().keyMoves` reports it with no new
 * instrument. That is why the editor pays it on a PREDICATE TRANSITION (open, close, the first
 * edit after a clean load, a fresh atlas binding) and never per selection or per frame.
 */
export interface WorldLightOptions {
  previewStatic?: boolean;
  /**
   * The session's shadow-map multiplier (`renderSettings`'s `shadowRes` row), folded with the
   * tier's `localMapSizeCap` by `lightModel.shadowMapSizeFor`. Omitted on an `apply` ⇒ unchanged,
   * exactly like `previewStatic`, so a caller that does not mention it cannot silently reset it.
   */
  shadowResScale?: number;
}

export interface WorldLightRig {
  info(): WorldLightsInfo;
  /**
   * Reconcile the rig against a new config list, adding/removing on the parent given at creation.
   * Returns the fresh info (including `keyMoves` for THIS call).
   */
  apply(cfgs: readonly WorldLightConfig[] | undefined, opts?: WorldLightOptions): WorldLightsInfo;
  /**
   * The session's shadow-map multiplier, applied to every authored light's map WITHOUT a
   * reconcile. `renderSettings`'s `shadowRes` row is a `runtime` verdict because of this: the
   * write is `shadow.mapSize` only, which is in `LIGHT_KEY_FREE_FIELDS`, so `keyMoves` stays 0.
   */
  setShadowResScale(scale: number): void;
  /**
   * The `static` lights, marked `BAKE_ONLY` and unparented. `scene.ts` parents these for a bake
   * and its `finally` removes them — the same array the `?bakeLights=1` path already drains.
   */
  bakeOnlyLights(): THREE.Light[];
  /**
   * ⭐ THE OTHER HALF OF MOBILITY, AND IT IS NOT SYMMETRICAL WITH THE ONE ABOVE BY ACCIDENT.
   *
   * `movable` means *"in the scene, NOT in the bake"* — and a light that is in the scene is in the
   * bake BY DEFAULT, because `bakeCapturePass` photographs the scene graph. So `static` needs a
   * light PARENTED for the bake and `movable` needs one UNPARENTED for it, and only doing the
   * first half leaves `movable` and `stationary` producing byte-identical atlases.
   *
   * That is not hypothetical: the first run of `tests/e2e/lights.e2e.ts` measured
   * `stationary − movable = −0.02` at a wall where `static − none` was `+4.01` — the movable arm's
   * atlas already had the bounce in it, so the arm that was supposed to be the no-bounce control
   * was not one, and `MOBILITY_NOTE.movable` was describing something the code did not do.
   */
  excludedFromBake(): THREE.Light[];
  /** Every light this rig owns, parented or not — for a teardown that must miss nothing. */
  allLights(): THREE.Light[];
  /**
   * ── ⭐ L2 · THE PER-FRAME HALF OF THE SHADOW UPDATE POLICY ──────────────────────────
   *
   * Called once per rendered frame from `scene.ts`'s `render()`, BEFORE the draw. Returns how many
   * shadow maps it scheduled this frame — 0 on the overwhelming majority of frames, and 0 forever
   * on a world where every caster is `everyFrame` (which is every world today).
   *
   * ⚠️ IT WRITES `shadow.needsUpdate` AND NOTHING ELSE. That field is in NO pipeline cache key,
   * which is the entire reason the LOD is expressed as a refresh schedule instead of as a
   * `castShadow` toggle: `castShadow` IS in the key, so toggling it per frame would regenerate
   * every visible material's WGSL every frame. The fence is `tests/renderLightShadowPolicy.test.ts`.
   *
   * The frame index is PASSED IN rather than counted here so that this module stays free of a
   * clock — the same discipline `src/sim` keeps, and it is what lets the test drive 240 frames in
   * a loop and assert the exact schedule.
   *
   * ── B4 · AND THE SLOT SELECTION, EVERY `SHADOW_SLOT_REFRESH_FRAMES`, WHEN A CAMERA IS GIVEN ──
   *
   * `slots` is the camera's position and the traversals left after the sun
   * (`localShadowBudget(tier, csmRig.policy().traversals)` — computed by the caller because the
   * sun's live policy is the caller's). On a selection frame the budgeted casters are ranked
   * (`selectLiveShadowSlots`) and every light whose slot CHANGED is moved through
   * `applyShadowUpdatePolicy` with its new effective arm: a promotion is two free writes; a
   * demotion is those plus the static-layer walk and one `needsUpdate`, so the frozen map is a
   * picture of the level rather than of nothing. Counted in the return like any scheduled map.
   * Omitted (a headless rig, a caller that has not heard of B4), no selection is made and every
   * budgeted light stays at its construction-time `'n/a'` — live, the peak.
   */
  tickShadows(
    frameIndex: number,
    slots?: { readonly cameraPos: Vec3Like; readonly budgetTraversals: number } | null,
  ): number;
  /**
   * Re-render every FROZEN shadow map once, from the current static geometry.
   *
   * Called at construction and after every `apply()` — i.e. when the world's lighting or geometry
   * changed and a map rendered before the change is now a picture of a level that is gone. It also
   * re-runs the static-layer walk, because the graph GROWS: entities spawn, and `static_mesh` decor
   * arrives from a 50 MB glTF several seconds after boot.
   */
  refreshCachedShadows(): number;
  dispose(): void;
}

interface Entry {
  cfg: WorldLightConfig;
  light: THREE.Light;
  /** 'runtime' ⇒ parented on `parent`; 'bake' ⇒ built, marked, NOT parented. */
  presence: 'runtime' | 'bake';
  /**
   * Is this entry `runtime` ONLY because preview is on? (`static` mobility resolved through
   * `previewPresence`.) The rig's own bookkeeping and the source of `info().previewParented` —
   * the graph is still what the counter reads, this only says which entries to look at.
   */
  preview: boolean;
  /** `LightSupport.inBake` — is this light supposed to be in front of the cube cameras? */
  inBake: boolean;
  /**
   * B4. The slot a `budgeted` caster holds. `'n/a'` for every other arm, and for a budgeted one
   * until the first selection (which resolves LIVE — `effectiveShadowUpdate`). This is the rig's
   * own bookkeeping and the source of `info().liveShadowSlots`; the `LightShadow` is written FROM
   * it, never read back to infer it.
   */
  slot: ShadowSlot;
}

/** Can this entry hold a slot at all? The condition `resolveLightRig` uses, in one place. */
function budgetable(cfg: WorldLightConfig, presence: 'runtime' | 'bake'): boolean {
  return (
    cfg.shadowUpdate === 'budgeted' &&
    cfg.castShadow &&
    presence === 'runtime' &&
    cfg.mobility !== 'movable'
  );
}

/**
 * Aim, in the SUN'S vocabulary: `azimuthDeg`/`elevationDeg` say where the light comes FROM. So the
 * unit vector below points from the target back at the light, and a spot's beam travels along its
 * NEGATION — which is why a spot at `elevationDeg: 45` shines downward (`WorldLightSchema` says so
 * at the field). One converter, `sunPositionFromAngles`, shared with the sun and the sky dome.
 */
function aimUnit(cfg: WorldLightConfig): [number, number, number] {
  return sunPositionFromAngles(cfg.azimuthDeg, cfg.elevationDeg, 1);
}

/** Does this config need a NEW three object, or can the existing one be written in place? */
const structuralKey = (cfg: WorldLightConfig, presence: string): string =>
  `${cfg.type}|${presence}`;

function construct(cfg: WorldLightConfig, presence: 'runtime' | 'bake', mapSize: number): THREE.Light {
  const color = new THREE.Color(cfg.color);
  let light: THREE.Light;
  if (cfg.type === 'directional') {
    const d = new THREE.DirectionalLight(color, cfg.intensity);
    /*
     * The target is left at the world origin and NEVER added to the scene — exactly what the sun
     * does. A `DirectionalLight`'s shading direction is `position - target.position`, and an
     * un-added target keeps an identity `matrixWorld`, which IS the origin. Its position therefore
     * only ever places the shadow camera, which is why `distanceM` is the field that sets it.
     */
    d.shadow.camera.matrixAutoUpdate = true; // LightShadow re-poses it every pass, or shadows come from the origin
    light = d;
  } else if (cfg.type === 'spot') {
    const s = new THREE.SpotLight(color, cfg.intensity, cfg.radiusM);
    /*
     * A SpotLight aims at `light.target.matrixWorld`, so the target has to be IN the graph. Making
     * it a child of the light is what keeps that true without a second scene-level object to
     * remove on teardown — and it makes the aim a purely LOCAL offset, so moving the light carries
     * its aim with it for free.
     */
    s.add(s.target);
    light = s;
  } else {
    light = new THREE.PointLight(color, cfg.intensity, cfg.radiusM);
  }
  light.name = `worldLight:${cfg.id}`;
  if (presence === 'bake') {
    // THE MARK, not a parallel mechanism: `src/render/indirect.ts` owns this key, the bake bracket
    // enforces the invariant, and `GiInfo.bakedLightLeaks` counts any frame that breaks it.
    light.userData[BAKE_ONLY] = true;
  }
  update(light, cfg, 'n/a', mapSize);
  return light;
}

/**
 * Write a config onto an existing light. Uniforms and poses only — nothing in the cache key.
 * `slot` (B4) is the rig's current assignment for a budgeted caster; it decides which of the three
 * applied arms the shadow is put into and is `'n/a'` for everything else.
 */
function update(light: THREE.Light, cfg: WorldLightConfig, slot: ShadowSlot, mapSize: number): void {
  light.color.set(cfg.color);
  light.intensity = cfg.intensity;
  if ((light as THREE.DirectionalLight).isDirectionalLight === true) {
    const d = light as THREE.DirectionalLight;
    const p = aimUnit(cfg);
    d.position.set(p[0] * cfg.distanceM, p[1] * cfg.distanceM, p[2] * cfg.distanceM);
    /*
     * ⚠️ `radiusM` MEANS THE ORTHOGRAPHIC HALF-EXTENT HERE and the attenuation cutoff four lines
     * below — one field, two meanings. `lightShadowExtentM` / `lightDistanceM` (PURE, `lightModel`)
     * are the ONE place that split is decided, so the editor's per-type label, the cost model and
     * this writer cannot come to disagree about which meaning is in play. They return `null` on
     * the type where the quantity is unrepresentable, which is why each is read inside the branch
     * that owns it and asserted non-null by construction rather than defaulted.
     */
    const extentM = lightShadowExtentM(cfg) ?? cfg.radiusM;
    d.shadow.camera.left = -extentM;
    d.shadow.camera.right = extentM;
    d.shadow.camera.top = extentM;
    d.shadow.camera.bottom = -extentM;
    /*
     * The shadow camera's far plane must reach PAST the light, for the same reason the sun's does
     * (`scene.ts`): `LightShadow.updateMatrices` poses an orthographic camera AT the light with
     * three's default far 500, so a light posed further out than that silently loses its shadows —
     * a failure that looks exactly like "shadows are off". Derived from the pose, so it cannot
     * drift.
     */
    d.shadow.camera.far = Math.max(500, cfg.distanceM + cfg.radiusM * 3);
  } else {
    light.position.set(cfg.pos[0], cfg.pos[1], cfg.pos[2]);
    const punctual = light as THREE.PointLight;
    /*
     * ⭐ THE ATTENUATION RADIUS, AND THE TWO THINGS IT ACTUALLY BUYS.
     *
     * It is `light.distance` → `cutoffDistanceNode`, a `renderGroup` UNIFORM, so writing it costs
     * nothing and rebuilds nothing. Measured, it also SAVES nothing under stock lighting
     * (`getDistanceAttenuation` puts the cutoff behind a `select`, so both arms run: 3.12 vs 3.06
     * ms/light on Sponza — noise). What it buys is real anyway and neither half is speculative:
     *
     *  1. `PointShadowNode` reads `const far = light.distance || camera.far` — a real radius
     *     tightens the cube shadow's depth range at zero cost, which is a precision win on the one
     *     light type that costs six traversals.
     *  2. It is the culling volume any future clustered path would use (D10 rejects clustering as
     *     a default; the radius is the input its trigger needs, and `.positive()` on the schema is
     *     what stops a 0 putting a light in NO cluster and silently vanishing it).
     *
     * `decay` is the same shape: `decayExponentNode`, a uniform, and it has silently been three's
     * constructor default of 2 for this rig's whole life because nothing ever wrote it.
     */
    punctual.distance = lightDistanceM(cfg) ?? cfg.radiusM;
    punctual.decay = cfg.decay;
    if ((light as THREE.SpotLight).isSpotLight === true) {
      const s = light as THREE.SpotLight;
      s.angle = (cfg.coneDeg * Math.PI) / 180;
      s.penumbra = cfg.penumbra;
      // The beam travels AWAY from where the light comes from — see `aimUnit`.
      const a = aimUnit(cfg);
      s.target.position.set(-a[0], -a[1], -a[2]);
      s.target.updateMatrix();
    }
  }
  applyShadow(light, cfg, slot, mapSize);
  // The matrix policy is opt-OUT (`THREE.Object3D.DEFAULT_MATRIX_AUTO_UPDATE = false` at
  // `scene.ts` module scope), so a posed object composes nothing on its own. `updateMatrix()` both
  // writes the local matrix and raises `matrixWorldNeedsUpdate`, which is what makes the next
  // render's `scene.updateMatrixWorld()` pick the new pose up.
  light.updateMatrix();
}

/**
 * Shadow flags: resolution, and the update policy that decides what a caster costs per frame.
 *
 * ⚠️ THE MANUAL MAP DISPOSE THAT USED TO LIVE HERE WAS BASED ON A CLAIM THAT IS FALSE IN r185,
 * AND IT WAS THE DANGEROUS KIND OF WRONG. The comment said *"a live shadow map is allocated at its
 * old size and three never reallocates it: the render target is created once and `mapSize` is only
 * read at creation"*, and therefore did `shadow.map?.dispose(); shadow.map = null`. Both halves are
 * wrong:
 *
 *  - `ShadowNode.renderShadow()` calls `shadowMap.setSize( shadow.mapSize.width, ... )` on EVERY
 *    shadow pass, and `RenderTarget.setSize()` updates the textures' image dimensions and calls
 *    `this.dispose()` whenever any axis changed. three resizes it for us, every pass, for free.
 *  - `ShadowNode` keeps its OWN reference (`this.shadowMap = shadowMap; this.shadow.map =
 *    shadowMap;`) and never reads `shadow.map` again. So nulling `shadow.map` orphaned our handle
 *    while the node went on rendering into the target we had just freed — the
 *    `Destroyed texture ... used in a submit` class this codebase already met once on
 *    `publishAtlas`'s realloc path, fired from an editor slider drag rather than from a bake.
 *
 * So: write `mapSize` and let three do the rest. `tests/renderWorldLights.test.ts` used to PIN the
 * old behaviour (`expect(light.shadow.map).toBeNull()`), which is why it survived a review.
 */
function applyShadow(
  light: THREE.Light,
  cfg: WorldLightConfig,
  slot: ShadowSlot,
  /**
   * ⭐ THE RESOLVED SIZE, NOT `cfg.shadowMapSize` — the owner's *"the shadow map resolution should
   * be a part of the rendering settings, not a local setting? And that it'd scale down based on
   * rendering settings?"*. `lightModel.shadowMapSizeFor(request, tier cap, session scale)` is the
   * ONE resolver and the rig computes it once per light; this function does not reach for a tier
   * or a session, so there is no second answer to reconcile.
   *
   * ⚠️ THE CLAMP IS LIVE-ONLY AND THAT SEAM IS LOAD-BEARING. `applySun` (below) writes the
   * AUTHORED size because that map is what the bake photographs; a clamp there would make the
   * baked atlas a function of the tier of the machine that baked it — two artists, one
   * `world.json`, two different atlases, and `checkProvenance` green on both. This is the reason
   * the two writes are two writes.
   */
  mapSize: number,
): void {
  const shadow = (light as THREE.DirectionalLight).shadow as THREE.LightShadow | undefined;
  /*
   * ⚠️ `castShadow` IS IN EVERY MATERIAL'S PIPELINE CACHE KEY (`LightsNode.customCacheKey` pushes
   * `light.castShadow ? 1 : 0` per light), so this write is the expensive one on the whole
   * function and it is why `castShadow` is a STATIC AUTHORED property here and never a runtime
   * lever. The runtime lever is `shadow.autoUpdate` / `shadow.needsUpdate`, which are in no key at
   * all. `tests/renderLightShadowPolicy.test.ts` scans `src/render/**` and `src/presentation/**`
   * to keep it that way.
   */
  light.castShadow = cfg.castShadow;
  if (!shadow) return;
  if (shadow.mapSize.width !== mapSize) {
    shadow.mapSize.set(mapSize, mapSize);
  }
  // B4: the ONE resolver of what `'budgeted'` does — the same function `resolveLightRig` prices
  // the row from, so the frame and the frame-cost line cannot disagree about a slot.
  applyShadowUpdatePolicy(shadow, effectiveShadowUpdate(cfg.shadowUpdate, cfg.mobility, slot));
}

/**
 * ── ⭐ THE SHADOW UPDATE POLICY, APPLIED — AND THE ONE FUNCTION THE SUN GOES THROUGH TOO ─────
 *
 * Exported because the sun is `scene.ts`'s own object, outside this rig's `lights[]` reconcile (it
 * must be: `probeFile.liveLighting()` fingerprints it, `createSky` is aimed from the same vector,
 * and `?sun=`/`?sunAz=` override it at boot). Since B1 the sun is POSED by `applySun` below rather
 * than by hand in `scene.ts`, and `applySun` is what calls this for it — a second copy of "how do
 * I freeze a shadow map" living beside the sun is exactly the two-answers shape that made the sun
 * invisible to `lightBudget()` for its whole life. One function, two callers, one meaning.
 *
 * All three fields it writes are in `LIGHT_KEY_FREE_FIELDS`: none of them is in
 * `LightsNode.customCacheKey()` or in `NodeManager.getCacheKey`, so switching a light's policy at
 * runtime rebuilds NO pipeline. That is the whole reason the policy is expressed as an update
 * schedule rather than as `castShadow` toggling, which would be a full-scene WGSL regeneration.
 */
export function applyShadowUpdatePolicy(
  shadow: THREE.LightShadow,
  /**
   * B4: an APPLIED arm, never the `'budgeted'` policy — the type refuses it, so a caller has to
   * go through `effectiveShadowUpdate` and say which slot the light holds.
   */
  update: AppliedShadowUpdate,
): void {
  if (update === 'everyFrame') {
    shadow.autoUpdate = true;
    /*
     * The camera goes BACK to the default layer, and it has to be a `set`, not a `disable`:
     * `ShadowNode.updateShadow`'s inherit branch is `(mask & 0xFFFFFFFE) === 0`, i.e. "layer 0 and
     * nothing else". A camera left holding both layer 0 and layer 3 fails that test, keeps its own
     * mask, and silently stops rendering anything the main camera put on a layer it does not have
     * — a shadow that quietly gets less correct after an artist switches a policy back.
     */
    shadow.camera.layers.set(0);
    return;
  }
  // Both remaining arms freeze the pass and drive it by hand. The one-shot `needsUpdate` is set by
  // the caller (`refreshCachedShadows`), not here, because "the world changed" is knowledge this
  // function does not have and the rig does.
  shadow.autoUpdate = false;
  if (update === 'staticOnly') shadow.camera.layers.set(FWG_STATIC_SHADOW_LAYER);
  else shadow.camera.layers.set(0);
}

/**
 * ── ⭐ B1 · THE SUN IS WRITTEN HERE, ONCE, FROM TWO CALLERS ────────────────────────────────────
 *
 * L1 made the sun a ROW of the resolved rig — a `writeTarget`, a cost, a field plan — and L2 made
 * it go through `applyShadowUpdatePolicy`. What neither did is make the row REACHABLE at runtime:
 * `scene.ts` posed the sun by hand once at construction, `GameScene.applyWorldLights` applied only
 * `lights[]`, and `SkyHandle.setSunPosition` had no caller outside its own constructor. So an
 * editor that listed the sun tomorrow would have edited `world.lighting.sun` and moved not one
 * pixel — the fourteenth logged instance of a subsystem that compiled, passed its tests and did
 * nothing. This function is the runtime end of the row: the ONE writer of the sun object, called
 * at construction (with the boot vector, dev overrides included) and again from every
 * `applyWorldLights` (with the authored vector). Same shape as `applyShadowUpdatePolicy` above —
 * one function, two callers, one meaning — and for the same reason: a second hand-rolled answer
 * to "how is the sun posed" beside the first is how the sun spent its life outside every table.
 *
 * `pos` is PASSED rather than derived from `cfg`: the boot caller folds `?sunAz=`/`?sunEl=` into
 * it, and `createSky(cfg, sunPos)` must be aimed from the SAME vector or the light and the dome
 * disagree. Whoever owns the vector calls this and the sky with it.
 *
 * ⚠️ `castShadow` IS IN EVERY MATERIAL'S CACHE KEY (`LIGHT_KEY_MOVING_FIELDS`), so a sun edit that
 * flips it is a full-scene WGSL regeneration like any light's. It is written HERE, in the one file
 * `tests/renderLightShadowPolicy.test.ts` exempts from its `FWG_LIGHT_TOGGLE_OK` scan, because
 * this is the one constructor/writer — not annotated at a second site in `scene.ts`. Every other
 * field below is in `LIGHT_KEY_FREE_FIELDS`: an editor slider on the sun rebuilds no pipeline.
 * `mapSize` is written only when it moved, and the map is never disposed by hand — see
 * `applyShadow` for the r185 reason.
 */
export function applySun(
  light: THREE.DirectionalLight,
  cfg: WorldSunConfig,
  pos: readonly [number, number, number],
): void {
  light.color.set(cfg.color);
  light.intensity = cfg.intensity;
  light.position.set(pos[0], pos[1], pos[2]);
  light.castShadow = cfg.castShadow;
  /*
   * ⛔ THE AUTHORED SIZE, UNCAPPED AND UNSCALED — AND THAT IS A RULING, NOT AN OVERSIGHT.
   *
   * This is the map the BAKE photographs: `csmRig.detachForBake` puts the stock box back and never
   * rewrites `mapSize`, so whatever is here is what every cube capture is lit through. Applying
   * the tier cap or the session's `shadowRes` multiplier at this line would make the baked atlas a
   * function of the machine that ran the bake — two artists on one `world.json` produce two
   * different atlases with every provenance check green and no observable but the picture.
   *
   * The LIVE sun map is a different number and is written by `csmRig.apply` from
   * `sunShadowPolicy(...).mapSizes`, which IS capped and scaled. Under `?csm=0` there is no
   * cascade rig, so the live map is this one and is therefore uncapped; the `shadowRes` render
   * setting's note says so, because a perf reading taken on that arm is not comparable with one
   * taken on the cascaded arm.
   */
  if (light.shadow.mapSize.width !== cfg.shadowMapSize) {
    light.shadow.mapSize.set(cfg.shadowMapSize, cfg.shadowMapSize);
  }
  const cam = light.shadow.camera;
  cam.left = -cfg.shadowExtentM;
  cam.right = cfg.shadowExtentM;
  cam.top = cfg.shadowExtentM;
  cam.bottom = -cfg.shadowExtentM;
  /*
   * THE SHADOW CAMERA'S FAR PLANE HAS TO REACH PAST THE LIGHT. `LightShadow.updateMatrices` poses
   * an orthographic camera AT `light.position` looking at the target, with three's default
   * near 0.5 / far 500. A world that moves its sun further out than 500 m (Sponza's is at 120 m
   * and an artist can drag it) — or one whose geometry extends past the light — would silently
   * lose its shadows behind the far plane, which is a failure that looks exactly like "shadows are
   * off". Derived from the pose, so it cannot drift; the same rule `update()` applies to an
   * authored directional light.
   */
  cam.far = Math.max(500, cfg.distanceM + cfg.shadowExtentM * 3);
  // LightShadow.updateMatrices repositions this camera (position + lookAt) every shadow pass —
  // it MUST auto-update or shadows silently render from the origin.
  cam.matrixAutoUpdate = true;
  /*
   * ── ⭐ B6 · THE SUN'S SCHEDULE, AUTHORED AT LAST — AND THIS WRITES THE SUN'S **OWN** MAP ───────
   *
   * `WorldSunSchema.shadowUpdate` is `.optional()` with no default, so absent resolves to
   * `'everyFrame'` — byte-identical to the literal that stood here — through
   * `lightModel.sunShadowUpdate`, which goes through `effectiveShadowUpdate` like every other row.
   *
   * ⚠️ THIS IS ONE OF TWO WRITERS AND THEY WRITE TWO DIFFERENT OBJECTS, exactly as the two
   * `mapSize` writes already do. `light.shadow` is the sun's own `LightShadow`: under cascades it
   * is never rendered live (`AnalyticLightNode.setupShadow` takes `shadow.shadowNode` instead of
   * building it) and it is the map the BAKE photographs. The CASCADE clones — `light.shadow`
   * copied once per cascade by `CSMShadowNode._init`, which is why they inherit whatever this line
   * wrote — belong to `csmRig`, and `csmRig` schedules them from the same resolved arm on the same
   * `SunShadowPolicy`. Neither of them is a second answer to "what does an arm mean": both call
   * `applyShadowUpdatePolicy`.
   */
  applyShadowUpdatePolicy(light.shadow, sunShadowUpdate(cfg));
  // Matrix policy is opt-OUT (`DEFAULT_MATRIX_AUTO_UPDATE = false` at `scene.ts` module scope):
  // a posed object composes nothing on its own, and `updateMatrix()` is what raises
  // `matrixWorldNeedsUpdate` for the next render.
  light.updateMatrix();
}

/**
 * ── ⭐ "THIS SUBTREE MOVES, SO KEEP IT OUT OF FROZEN SHADOW MAPS" ────────────────────
 *
 * The opt-OUT half of `FWG_STATIC_SHADOW_LAYER`. `enableStaticShadowLayer` below walks the graph
 * and opts every object INTO the static channel; a subtree stamped here is skipped WHOLE, so
 * anything added under it later is skipped too. That matters concretely: the real character mesh
 * is loaded asynchronously and parented under the player group seconds after the group exists, and
 * a one-shot walk that marked leaves rather than subtrees would burn it into every frozen map.
 *
 * The direction is forced by `Layers.test` being a bitwise AND over an OR-built mask — there is no
 * "every layer except this one", so static geometry must opt in and movers must opt out. See
 * `FWG_STATIC_SHADOW_LAYER`.
 */
export const DYNAMIC_CASTER_USERDATA = 'fwgDynamicCaster';

export function markDynamicShadowCaster(root: THREE.Object3D): void {
  root.userData[DYNAMIC_CASTER_USERDATA] = true;
}

/**
 * Opt every static object into the frozen-shadow channel, skipping marked subtrees whole.
 *
 * Cost: one graph walk, run only when a `staticOnly` light is about to re-render its map — i.e. at
 * load and on an authored change, never per frame. It is deliberately re-run rather than done once
 * because the graph GROWS (entities spawn, `static_mesh` decor arrives from a 50 MB glTF), and a
 * one-shot walk would silently stop covering the level.
 *
 * ⭐ B6: EXPORTED, because the SUN's frozen map needs the same channel populated and the sun is not
 * one of this rig's `entries` (it is `scene.ts`'s own object — `applySun` says why). `csmRig` is
 * its second caller. Without the walk a frozen shadow camera on layer 3 renders an EMPTY map, which
 * reads exactly like "the sun stopped casting" — the failure mode `FWG_STATIC_SHADOW_LAYER`'s own
 * note calls "worse than useless".
 */
export function enableStaticShadowLayer(o: THREE.Object3D): void {
  if (o.userData[DYNAMIC_CASTER_USERDATA] === true) return;
  o.layers.enable(FWG_STATIC_SHADOW_LAYER);
  for (const child of o.children) enableStaticShadowLayer(child);
}

/**
 * ── ⭐ R5 · THE COMPLEMENT WALK — MOVERS INTO `FWG_DYNAMIC_SHADOW_LAYER` ────────────────────────
 *
 * The exact mirror of `enableStaticShadowLayer` above, over the SAME predicate: where that one
 * walks until it meets a `DYNAMIC_CASTER_USERDATA` mark and opts in everything before it, this one
 * walks until it meets the mark and opts in everything from there DOWN.
 *
 * ⭐ ONE MARK DECIDES BOTH, AND THAT IS THE WHOLE REASON THIS IS TWO FUNCTIONS AND NOT TWO
 * PREDICATES. The two-map split composes its terms with `min` over what are supposed to be DISJOINT
 * object sets. An object in neither channel casts no shadow from a light that is on; an object in
 * both is drawn twice and double-darkened at its own silhouette. Neither fails a test — both are
 * pictures. Deriving both channels from `DYNAMIC_CASTER_USERDATA` makes the partition structural:
 * an object is on exactly one side of a single boolean, whatever that boolean happens to say.
 *
 * ⚠️ IT IS RE-RUN, NOT DONE ONCE, for `enableStaticShadowLayer`'s reason and one more of its own:
 * the graph grows (the character glTF lands under the player group seconds late), and a mover that
 * arrives after the walk would be in NO channel — invisible to both maps, so it would stop casting
 * a shadow entirely rather than merely casting a stale one. `csmRig.refreshSunShadow()` is the
 * moment; `applyShadowFlags`' `childadded` hook is what keeps the MARK correct for the arrival.
 */
export function enableDynamicShadowLayer(o: THREE.Object3D): number {
  if (o.userData[DYNAMIC_CASTER_USERDATA] === true) {
    let n = 0;
    o.traverse((c) => {
      c.layers.enable(FWG_DYNAMIC_SHADOW_LAYER);
      n++;
    });
    return n;
  }
  let n = 0;
  for (const child of o.children) n += enableDynamicShadowLayer(child);
  return n;
}

/**
 * A light's `LightShadow`, or null for a type that has none. One reader, because
 * `(light as THREE.DirectionalLight).shadow` is a lie the compiler accepts on every light type and
 * a `HemisphereLight` reaching this code would read `undefined` and throw one frame later.
 */
function shadowOf(light: THREE.Light): THREE.LightShadow | null {
  const shadow = (light as THREE.DirectionalLight).shadow as THREE.LightShadow | undefined;
  return shadow ?? null;
}

function disposeLight(light: THREE.Light): void {
  const shadow = (light as THREE.DirectionalLight).shadow as THREE.LightShadow | undefined;
  shadow?.map?.dispose();
  light.dispose();
}

/**
 * Build a world's light rig on `parent` (the scene). Refusals are returned as data; nothing is
 * dropped silently and nothing is downgraded to a neighbouring mobility.
 */
export function createWorldLights(
  parent: THREE.Object3D,
  cfgs: readonly WorldLightConfig[] | undefined,
  tier: number,
  opts: WorldLightOptions = {},
): WorldLightRig {
  let entries: Entry[] = [];
  let refusals: WorldLightRefusal[] = [];
  let keyMoves = 0;
  /**
   * The LAST value `reconcile` was given. Held rather than re-read from a caller each time so that
   * `refreshCachedShadows()` and `info()` — which run between applies — describe the same rig the
   * frame is drawing, and so an `apply()` that omits the option cannot silently drop preview.
   */
  let previewStatic = opts.previewStatic === true;
  /**
   * ⭐ WHAT AN AUTHORED LIGHT'S SHADOW MAP IS ACTUALLY ALLOCATED AT. Two numbers, deliberately
   * kept apart: `localMapSizeCap` is a DEVICE FACT (the tier's row) and `shadowResScale` is a
   * HUMAN PREFERENCE for this session. `cfg.shadowMapSize` is neither — it is the artist's
   * REQUEST, it stays level data, and it stays in `worldHash`.
   *
   * ⚠️ THE CAP HAD NO READER HERE AT ALL UNTIL NOW, WHICH IS THE MEASURED DEFECT: one authored
   * `castShadow` point light at 1024 allocates 1024² x 8 B x 6 faces = 50,331,648 B — the whole
   * tier-2 cascade set from one lamp — at every tier, including the two whose SUN is capped at
   * 1024. `mapSizeCap` covered the sun's near cascade and nothing else.
   */
  const localMapSizeCap = SUN_SHADOW_TIERS[(Math.max(0, Math.min(2, Math.floor(tier))) as 0 | 1 | 2)]
    .localMapSizeCap;
  let shadowResScale = typeof opts.shadowResScale === 'number' ? opts.shadowResScale : 1;
  const resolvedMapSize = (cfg: WorldLightConfig): number =>
    shadowMapSizeFor(cfg.shadowMapSize, localMapSizeCap, shadowResScale);
  /** B4. The budget the last slot selection was made against; null until one has been. */
  let lastLocalBudget: number | null = null;

  function reconcile(next: readonly WorldLightConfig[] | undefined): void {
    const wanted = next ?? [];
    const byId = new Map(entries.map((e) => [e.cfg.id, e]));
    const kept: Entry[] = [];
    const nextRefusals: WorldLightRefusal[] = [];
    keyMoves = 0;
    for (const cfg of wanted) {
      /*
       * ⭐ ONE resolve, then ONE adjustment — `previewPresence` is the SAME function
       * `resolveLightRig` folds through, which is what keeps the row's price and the graph's
       * contents from ever disagreeing about whether this light exists.
       */
      const support = previewPresence(resolveLightSupport(cfg.type, cfg.mobility), previewStatic);
      if (!support.supported) {
        nextRefusals.push({
          id: cfg.id,
          type: cfg.type,
          mobility: cfg.mobility,
          reason: support.note,
        });
        continue;
      }
      const presence = support.presence === 'bake' ? 'bake' : 'runtime';
      // A `static` light that resolved `runtime` did so for exactly one reason (see
      // `previewPresence`): preview is on. Derived, never a second copy of the flag.
      const preview = presence === 'runtime' && cfg.mobility === 'static';
      const prior = byId.get(cfg.id);
      if (prior && structuralKey(prior.cfg, prior.presence) === structuralKey(cfg, presence)) {
        byId.delete(cfg.id);
        // B4: a kept light keeps its slot while it can still hold one, so an intensity drag on a
        // live caster does not freeze it; a light that stopped being budgetable (arm, mobility or
        // castShadow edited) drops to `'n/a'`, and the next selection frame ranks whatever is left.
        const slot: ShadowSlot = budgetable(cfg, presence) ? prior.slot : 'n/a';
        update(prior.light, cfg, slot, resolvedMapSize(cfg));
        kept.push({ cfg, light: prior.light, presence, preview, inBake: support.inBake, slot });
        continue;
      }
      if (prior) {
        byId.delete(cfg.id);
        prior.light.parent?.remove(prior.light);
        disposeLight(prior.light);
        keyMoves++;
      }
      const light = construct(cfg, presence, resolvedMapSize(cfg));
      if (presence === 'runtime') parent.add(light);
      keyMoves++;
      kept.push({ cfg, light, presence, preview, inBake: support.inBake, slot: 'n/a' });
    }
    // Whatever is left in `byId` is a light the world no longer authors.
    for (const stale of byId.values()) {
      stale.light.parent?.remove(stale.light);
      disposeLight(stale.light);
      keyMoves++;
    }
    entries = kept;
    refusals = nextRefusals;
  }

  function info(): WorldLightsInfo {
    const runtime = entries.filter((e) => e.presence === 'runtime');
    return {
      runtime: runtime.length,
      parented: entries.filter((e) => e.light.parent !== null).length,
      bakeOnly: entries.length - runtime.length,
      // Off the GRAPH, exactly like `parented` above and for the same reason: "the static light is
      // absent at runtime" is the central claim of the mobility model, and a flag that says preview
      // is on proves nothing about whether a light is actually in the scene.
      previewParented: entries.filter((e) => e.preview && e.light.parent !== null).length,
      shadowCasters: runtime.filter((e) => e.cfg.castShadow).length,
      // B4: a FROZEN budgeted caster is a `staticOnly` map in every respect the frame can
      // measure (0 traversals, static geometry only), so it is counted with them — the editor's
      // "N caster(s) frozen (0 ms/frame, no moving shadows)" is then literally true.
      cachedShadowCasters: runtime.filter(
        (e) =>
          e.cfg.castShadow &&
          (e.cfg.shadowUpdate === 'staticOnly' ||
            (e.cfg.shadowUpdate === 'budgeted' && e.slot === 'frozen')),
      ).length,
      budgetedShadowCasters: runtime.filter((e) => budgetable(e.cfg, e.presence)).length,
      liveShadowSlots: runtime.filter((e) => e.slot === 'live').map((e) => e.cfg.id),
      frozenShadowSlots: runtime.filter((e) => e.slot === 'frozen').map((e) => e.cfg.id),
      localShadowBudget: lastLocalBudget,
      /*
       * Priced off the CONFIG rather than off `light.shadow.map`, deliberately and for the same
       * reason `censusScene` does: the render target is allocated lazily by `ShadowNode.setup()` on
       * the light's first render, so reading the allocation would report 0 for every map that is
       * about to exist. An under-stating budget instrument is the failure that already happened
       * here once (the accrual ledger read 0.0 MB for a 68-texture scene).
       */
      shadowMapBytes: entries.reduce(
        (sum, e) =>
          sum +
          shadowMapBytes({
            type: e.cfg.type,
            castShadow: e.cfg.castShadow,
            presence: e.presence,
            // The RESOLVED size, which is what `applyShadow` wrote onto the LightShadow — not the
            // request. A ledger that priced the request would over-state by 4x at 0.5x scale.
            shadowMapSize: resolvedMapSize(e.cfg),
          }),
        0,
      ),
      budget: lightBudget(tier),
      refusals: refusals.map((r) => ({ ...r })),
      keyMoves,
    };
  }

  /** The lights whose depth pass this rig drives by hand. Empty on every world shipped today. */
  const drivenShadows = (): Entry[] =>
    entries.filter(
      (e) =>
        e.presence === 'runtime' &&
        e.cfg.castShadow &&
        e.cfg.shadowUpdate !== 'everyFrame' &&
        shadowOf(e.light) !== null,
    );

  function refreshCachedShadows(): number {
    const driven = drivenShadows();
    if (driven.length === 0) return 0;
    /*
     * The static-layer walk runs HERE, immediately before the maps that read it are scheduled, and
     * not once at construction. The scene graph is still growing at construction: `static_mesh`
     * decor is a 50 MB glTF that lands seconds later, and registry entities spawn on demand. A
     * one-shot walk would leave every one of them missing from every frozen shadow map, which
     * reads exactly like "shadows are off for that object" and would be blamed on the light.
     */
    // B4: a frozen budgeted caster's camera is on the static layer exactly like a `staticOnly`
    // one, so the walk that populates that layer has to run for it too.
    if (driven.some((e) => e.cfg.shadowUpdate === 'staticOnly' || e.slot === 'frozen')) {
      enableStaticShadowLayer(parent);
    }
    for (const e of driven) {
      const shadow = shadowOf(e.light);
      if (shadow) shadow.needsUpdate = true;
    }
    return driven.length;
  }

  /**
   * B4 — the selection, on its frame. Returns how many FROZEN maps it scheduled (demotions), so
   * `tickShadows`' contract ("maps scheduled this frame") stays one number.
   */
  function selectSlots(cameraPos: Vec3Like, budgetTraversals: number): number {
    const candidates: ShadowSlotCandidate[] = [];
    const byId = new Map<string, Entry>();
    for (const e of entries) {
      if (!budgetable(e.cfg, e.presence) || shadowOf(e.light) === null) continue;
      byId.set(e.cfg.id, e);
      candidates.push({
        id: e.cfg.id,
        // A directional light in `lights[]` has no position — see `ShadowSlotCandidate.pos`.
        pos:
          e.cfg.type === 'directional'
            ? null
            : { x: e.cfg.pos[0], y: e.cfg.pos[1], z: e.cfg.pos[2] },
        faces: e.cfg.type === 'point' ? POINT_SHADOW_FACES : 1,
      });
    }
    lastLocalBudget = budgetTraversals;
    if (candidates.length === 0) return 0;
    const incumbents = new Set(candidates.filter((c) => byId.get(c.id)!.slot === 'live').map((c) => c.id));
    const selection = selectLiveShadowSlots(candidates, cameraPos, budgetTraversals, incumbents);
    let demoted = 0;
    const move = (id: string, slot: ShadowSlot): void => {
      const e = byId.get(id)!;
      if (e.slot === slot) return;
      e.slot = slot;
      const shadow = shadowOf(e.light)!;
      /*
       * Every write below is in `LIGHT_KEY_FREE_FIELDS`: `autoUpdate`, `camera.layers` and
       * `needsUpdate`. A slot change is therefore a POSE-CLASS edit — no pipeline is rebuilt when
       * the player walks from one lamp to the next, which is the whole reason the split is a
       * policy over `shadowUpdate` and not a `castShadow` toggle.
       */
      applyShadowUpdatePolicy(shadow, effectiveShadowUpdate(e.cfg.shadowUpdate, e.cfg.mobility, slot));
      if (slot === 'frozen') {
        // The frozen map has to be a picture of the level: one render, static layer only.
        shadow.needsUpdate = true;
        demoted++;
      }
    };
    for (const id of selection.live) move(id, 'live');
    // The walk BEFORE the demoted maps render, for the reason `refreshCachedShadows` gives: the
    // graph grew since the last walk, and a mesh off the channel is a wall missing from the map.
    if (selection.frozen.some((id) => byId.get(id)!.slot !== 'frozen')) enableStaticShadowLayer(parent);
    for (const id of selection.frozen) move(id, 'frozen');
    return demoted;
  }

  function tickShadows(
    frameIndex: number,
    slots: { readonly cameraPos: Vec3Like; readonly budgetTraversals: number } | null = null,
  ): number {
    let scheduled = 0;
    // B4: frame 0 is a selection frame, and the tick runs BEFORE the draw, so the first frame is
    // already drawn under the selection rather than at every budgeted light's peak.
    if (slots !== null && frameIndex % SHADOW_SLOT_REFRESH_FRAMES === 0) {
      scheduled += selectSlots(slots.cameraPos, slots.budgetTraversals);
    }
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i]!;
      if (e.presence !== 'runtime' || !e.cfg.castShadow) continue;
      if (e.cfg.shadowUpdate !== 'everyNFrames') continue;
      const shadow = shadowOf(e.light);
      if (!shadow) continue;
      const n = normalizeShadowUpdateN(e.cfg.shadowUpdateN);
      /*
       * ⚠️ A PER-LIGHT PHASE, AND WITHOUT IT THE LOD IS A STUTTER RATHER THAN A SAVING. Four
       * `everyNFrames: 4` casters refreshing together is 4 × 2.96 ms on one frame in four and
       * nothing on the other three — the same total, delivered as a hitch. The offset is the
       * light's INDEX in this rig, which is the world's authoring order and therefore stable
       * across reloads.
       *
       * (It was `light.id` first, and that was wrong in a way worth recording: three's Object3D
       * counter is not consecutive across lights, because a `PointLight` constructs a
       * `PointLightShadow` holding its own `PerspectiveCamera`. Consecutive point lights are two
       * ids apart, so at N = 4 they collapsed onto two phases instead of four — measured, and it
       * is why the test asserts the per-frame DISTRIBUTION rather than a total.)
       */
      // B6: the predicate moved to `lightModel.shadowRefreshDue` so the SUN's cascades phase the
      // same way (by cascade index) rather than through a second copy of this arithmetic.
      if (!shadowRefreshDue(frameIndex, i, n)) continue;
      shadow.needsUpdate = true;
      scheduled++;
    }
    return scheduled;
  }

  reconcile(cfgs);
  refreshCachedShadows();
  // A fresh rig's constructions are not a "hitch" anybody could have avoided — the scene has no
  // compiled materials yet — so the boot apply reports 0 rather than N.
  keyMoves = 0;

  return {
    info,
    apply(next, applyOpts) {
      // Omitted ⇒ unchanged. An `apply` that dropped preview because the caller did not mention it
      // would take the light away mid-drag, which reads as "the light broke" and gets blamed on the
      // edit; `scene.applyWorldLights` passes the stored session flag on every call.
      if (applyOpts?.previewStatic !== undefined) previewStatic = applyOpts.previewStatic;
      if (applyOpts?.shadowResScale !== undefined) shadowResScale = applyOpts.shadowResScale;
      reconcile(next);
      // A policy change or a moved light makes an already-frozen map a picture of a level that is
      // gone. `keyMoves` stays whatever `reconcile` reported: scheduling a depth pass is not a
      // pipeline rebuild, and conflating the two would make an artist think a free edit was not.
      refreshCachedShadows();
      return info();
    },
    bakeOnlyLights() {
      return entries.filter((e) => e.presence === 'bake').map((e) => e.light);
    },
    excludedFromBake() {
      return entries.filter((e) => e.presence === 'runtime' && !e.inBake).map((e) => e.light);
    },
    allLights() {
      return entries.map((e) => e.light);
    },
    setShadowResScale(scale) {
      /*
       * ⭐ FREE BY CONSTRUCTION, AND THAT IS THE WHOLE REASON THIS IS A MULTIPLIER AND NOT A
       * REBUILD. `shadow.mapSize` is in `LIGHT_KEY_FREE_FIELDS` — it appears in no
       * `LightsNode.customCacheKey()` and in no `NodeManager.getCacheKey`, and
       * `ShadowNode.renderShadow()` calls `shadowMap.setSize(...)` on every pass, so three
       * reallocates the target for us. No `castShadow`, no `visible`, no `keyMoves`: a session
       * scale change rebuilds not one material's WGSL.
       */
      shadowResScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
      for (const e of entries) update(e.light, e.cfg, e.slot, resolvedMapSize(e.cfg));
    },
    tickShadows,
    refreshCachedShadows,
    dispose() {
      for (const e of entries) {
        e.light.parent?.remove(e.light);
        disposeLight(e.light);
      }
      entries = [];
      refusals = [];
    },
  };
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ L1 · THE WHOLE RIG, NOT JUST THIS MODULE'S HALF OF IT ────────────────────────────────────
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `WorldLightsInfo` above answers "what did `lighting.lights[]` resolve to". That was the whole
 * question while the sun, the sky and the fill were invisible to every surface an artist can see.
 * It is not the whole question any more: the sun IS a light, it IS in the scene, and it IS the one
 * shadow caster nearly every world has. A budget that omits it is a budget of the minority.
 *
 * So this is the composition — `WorldLightsInfo` (this rig, which owns `lights[]` and nothing
 * else) plus `resolveLightRig`'s ROWS (the whole `lighting` block, sun and sky included) plus the
 * two summed numbers that price them. It is a PURE function taking both halves, because that keeps
 * the seam honest in both directions: this module still owns only the lights it constructs, and
 * nobody has to guess which of two numbers named `shadowCasters` they are looking at.
 *
 * ⚠️ `shadowCasters` (count of authored runtime lights with `castShadow`) and `shadowTraversals`
 * (DEPTH PASSES PER FRAME over the whole rig) are deliberately different numbers and both are
 * kept. One shadowed POINT light is 1 caster and 6 traversals — `PointShadowNode.renderShadow` is
 * literally a six-iteration loop of `renderer.render(scene, camera)` — and the second number is
 * the one that predicts the milliseconds.
 */
export interface WorldLightRigInfo extends WorldLightsInfo {
  /**
   * ⭐ THE ONE LIST: the sun row, the sky row, then every authored light in array order — refused
   * ones included, because a refused light that vanishes from the list is the silent downgrade the
   * refusal table exists to prevent. Every row carries its own `writeTarget`, so a consumer
   * dispatches rather than choosing.
   */
  rows: ResolvedLight[];
  /**
   * `rigCost(rows)` — the whole rig's price, in the THREE units that do not mix: per-fragment ms
   * (cost units), per-scene-traversal ms (shadow traversals) and resident bytes (shadow maps).
   */
  cost: ReturnType<typeof rigCost>;
  /** The tier's ceiling for those two numbers. REPORTED, never enforced. */
  costBudget: LightCostBudget;
  /** True when `cost` exceeds either arm of `costBudget`. A number, not a refusal. */
  overCost: boolean;
  /**
   * B3. The sun's cascade policy the rows were priced under — the LIVE one from `csmRig` when the
   * scene calls this, the tier's row when a test does. `enabled: false` is the single origin map
   * and the arm every L1/L2 number was taken on.
   */
  sunShadow: SunShadowPolicy;
  /**
   * ⭐ THE MULTIPLIER THE AUTHORED LIGHTS WERE PRICED AND ALLOCATED AT — separate from
   * `sunShadow.resScale` because THE TWO LEGITIMATELY DIFFER. `sunShadowPolicy`'s single-map arm
   * (`?csm=0` / Cascades → Off) hardcodes `resScale: 1`, correctly: under that arm the live sun map
   * is `applySun`'s authored one, the map the bake photographs, and nothing scaled it. Local lights
   * are still scaled there. Details printed `sunShadow.resScale` as the multiplier for BOTH caps,
   * so `csm: 0` + `shadowRes: 0.5` — two clicks apart in the Settings tab — annotated a halved
   * megabyte figure with "x 1.00".
   */
  localResScale: number;
}

/**
 * Compose the rig's own info with the resolved rows for the world's WHOLE lighting block.
 *
 * `lighting` is passed in rather than captured at construction because the editor REPLACES the
 * world object on undo/redo/Open/New — a captured reference would keep pricing the previous
 * world's sun. `GameScene.applyWorldLights` hands over the live block on every call for exactly
 * that reason.
 *
 * `sunShadow` (B3) defaults to the TIER'S cascade policy, so this — the tier-aware caller — is
 * where the frame-cost line moves from `1/4` to `3/4` traversals at tier 2. `resolveLightRig`
 * itself defaults to the single map, which is what keeps the pure pins unmoved.
 */
export function describeLightRig(
  base: WorldLightsInfo,
  lighting: LightRigInput,
  tier: number,
  sunShadow: SunShadowPolicy = sunShadowPolicy(tier, lighting.sun),
  /**
   * Preview lighting for unbaked `static` lights. Passed rather than read off `base` because
   * `WorldLightsInfo` reports the GRAPH (`previewParented` can be 0 while preview is on simply
   * because the world authors no static light), and the fold needs the POLICY. `scene.ts` — the
   * only caller with a session — passes its stored flag; a test or a headless caller gets `false`,
   * which is the arm every committed pin was taken on.
   */
  previewStatic = false,
  /**
   * The session's shadow-map multiplier, so the PRICED bytes are the ALLOCATED bytes. `1` — the
   * default — is what every committed pin was taken on. It is passed rather than read off `base`
   * for the same reason `previewStatic` is: `WorldLightsInfo` reports the GRAPH, and the fold
   * needs the POLICY; `scene.ts` is the only caller with a session and it passes its stored value.
   */
  shadowResScale = 1,
): WorldLightRigInfo {
  /*
   * B4: the rows are priced under the rig's LIVE slot assignment — the `Entry.slot` bookkeeping
   * the runtime writes the `LightShadow`s from — so a frozen budgeted row reads 0 traversals on
   * the same line that shows the sun's cascades. `null` until the rig has selected once (a
   * headless rig, or the frame before the first tick), which the pure fold prices at the peak.
   */
  const slots: ShadowSlotState | null =
    base.localShadowBudget === null
      ? null
      : {
          live: new Set(base.liveShadowSlots),
          frozen: new Set(base.frozenShadowSlots),
          budgetTraversals: base.localShadowBudget,
          tier,
        };
  const rows = resolveLightRig(lighting, sunShadow, slots, previewStatic, {
    // The SAME cap and scale `createWorldLights` writes with — one resolver, two callers, so the
    // Details frame-cost line and the LightShadow on the graph cannot report different megabytes.
    cap: SUN_SHADOW_TIERS[(Math.max(0, Math.min(2, Math.floor(tier))) as 0 | 1 | 2)].localMapSizeCap,
    scale: shadowResScale,
  });
  const cost = rigCost(rows);
  const costBudget = lightCostBudget(tier);
  return {
    ...base,
    rows,
    cost,
    costBudget,
    overCost:
      cost.costUnits > costBudget.costUnits ||
      cost.shadowTraversals > costBudget.shadowTraversals,
    sunShadow,
    localResScale: shadowResScale,
  };
}
